import { STALE_TIME } from "@/constants";
import { cn } from "@/lib/utils";
import { getSystemUsers } from "@/services/auth-functions";
import {
	editSecondaryManagerApprover,
	EditSecondaryManagerApproverParams,
} from "@/services/invoice-functions";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronsUpDown } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { HiOutlinePencilAlt } from "react-icons/hi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "../ui/command";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Textarea } from "../ui/textarea";

const formSchema = z.object({
	managerid: z.number(),
	remarks: z.string().min(1, {
		message: "Remarks are required",
	}),
});

const UpdateSecondaryManagerApprover: React.FC<{
	accessToken: string;
	invoiceId: number;
	managerId: number;
}> = ({ accessToken, invoiceId, managerId }) => {
	const [open, setOpen] = useState(false);
	const [suOpen, setSuOpen] = useState(false);

	const queryClient = useQueryClient();

	const systemUsers = useQuery({
		queryKey: ["systemusers"],
		queryFn: () => getSystemUsers(accessToken),
		staleTime: STALE_TIME,
		onSuccess: () => {
			form.setValue("managerid", managerId);
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
	});

	const approverUpdate = useMutation({
		mutationKey: ["invoice_secondary_manager_edit"],
		mutationFn: (approver: EditSecondaryManagerApproverParams) =>
			editSecondaryManagerApprover(accessToken, approver),
		onSuccess: ({ message }) => {
			queryClient.invalidateQueries(["invoice"]);
			setOpen(false);
			form.reset();
			toast.success(message);
			approverUpdate.reset();
		},
		onError: (err) => {
			toast.error("Error updating approver, try again");
			console.log(err);
		},
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		approverUpdate.mutate({
			invoicemasterid: invoiceId,
			secondarymanagertobeapprovedby: data.managerid,
			editremark: data.remarks,
		});
	};

	const disabled = () => {
		if (systemUsers.isLoading) return true;
		if (approverUpdate.isLoading) return true;
		return false;
	};

	return (
		<Dialog open={open} onOpenChange={(open) => setOpen(open)}>
			<DialogTrigger asChild>
				<Button>Update Secondary Approver</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-md">
				<DialogHeader className="text-left">
					<DialogTitle className="text-purple-500 flex gap-2 items-center mb-1">
						<div className="bg-purple-200 p-1 rounded-full">
							<HiOutlinePencilAlt className="text-xl" />
						</div>
						Update Secondary Approver
					</DialogTitle>
					<DialogDescription>Update the invoice approver.</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<div className="flex items-center space-x-2 mb-4">
							<div className="grid flex-1 gap-2">
								{systemUsers.isLoading ? (
									<span>Loading Users</span>
								) : systemUsers.data ? (
									<FormField
										name="managerid"
										render={({ field }) => (
											<FormItem className="flex flex-col">
												<FormLabel>To Be Approved By:</FormLabel>
												<Popover open={suOpen} onOpenChange={(open) => setSuOpen(open)}>
													<PopoverTrigger asChild>
														<FormControl>
															<Button
																variant="outline"
																role="combobox"
																className={cn(
																	"justify-between font-normal",
																	!field.value && "text-muted-foreground"
																)}
																disabled={disabled()}
															>
																{field.value
																	? systemUsers.data.find((user) => user.id === field.value)
																			?.username
																	: "Select approver"}
																<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
															</Button>
														</FormControl>
													</PopoverTrigger>
													<PopoverContent className="p-0 w-[calc(100vw-2rem)] sm:w-[400px]">
														<Command>
															<CommandInput placeholder="Search account..." />
															<CommandList>
																<CommandEmpty>No account found.</CommandEmpty>
																<CommandGroup>
																	{systemUsers.data.map((item) => (
																		<CommandItem
																			value={item.username}
																			key={item.id}
																			onSelect={() => {
																				form.setValue("managerid", item.id);
																				setSuOpen(false);
																			}}
																		>
																			<Check
																				className={cn(
																					"mr-2 h-4 w-4",
																					item.id.toString() === field.value
																						? "opacity-100"
																						: "opacity-0"
																				)}
																			/>
																			{item.username}
																		</CommandItem>
																	))}
																</CommandGroup>
															</CommandList>
														</Command>
													</PopoverContent>
												</Popover>
												<FormMessage />
											</FormItem>
										)}
									/>
								) : null}
								<FormField
									name="remarks"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Remarks</FormLabel>
											<FormControl>
												<Textarea {...field} disabled={disabled()} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
						<DialogFooter className="sm:justify-end">
							<DialogClose asChild>
								<Button type="button" variant="ghost">
									Cancel
								</Button>
							</DialogClose>
							<Button type="submit" disabled={disabled()}>
								{disabled() ? "Submitting..." : "Submit"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default UpdateSecondaryManagerApprover;
